import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import i18next from '../../../i18n';
import styles from './ProductListingPage.module.scss';
import v2 from './ProductListingPageV2.module.scss';
import AppPaths from '../../AppPaths';
import { getDomainConfig } from '../../lib/domainConfig';
import { series3PlusCollarId } from '../../types/Product';
import { inAppShop, partnerLandingPage, productListingPage, quizQuestionPage } from '../../lib/analytics/events';
import { getPartnerSlugForCode } from '../../views/PartnerLanding/partnersRegistry';
import { usePartnerLandingEnabled } from '../../views/PartnerLanding/usePartnerLandingEnabled';
import FiMiniImage from '../../assets/images/plp-fi-mini.png';
import FiSeries3PlusImage from '../../assets/images/plp-fi-series-three-plus.png';
import FiUltraImage from '../../assets/images/plp-fi-ultra.png';
import FiMiniForCatsImage from '../../assets/images/plp-fi-mini-cats.png';
import FiAccessoriesImage from '../../assets/images/plp-accessories.webp';
import FiNanoMicrochipsImage from '../../assets/images/plp-nano.webp';
import { ReactComponent as DownArrow } from '../../assets/images/icons/arrow_down.svg';
import { ReactComponent as UpArrow } from '../../assets/images/icons/arrow_up.svg';
import { ReactComponent as Check } from '../../assets/images/check.svg';
import { PlpIcon, PlpSpecIcon, QuizSpark } from './PlpV2Icons';
import { ReactComponent as Dog } from '../../assets/images/icons/dog.svg';
import { ReactComponent as Cat } from '../../assets/images/icons/cat.svg';
import classNames from 'classnames';
import {
  collarProductIsSupportedInCountry,
  ultraIsDiscoverable,
  nanoProductIsSupportedInCountry,
  useLocale,
} from '../../contexts/LocaleContext';
import * as types from '../../types';
import { Button, Flex, Text } from '@mantine/core';
import { SaleBadge } from '../SaleBadge/SaleBadge';
import { useFlagsLoaded, useIsGrowthBookEnabled } from '../../lib/growthbook';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import { QuizAutoOpen } from '../ProductQuiz/QuizAutoOpen';
import FreeTrialCard from '../FreeTrialCard/FreeTrialCard';
import { getUrlPartnerCode } from '../../lib/giftWithPurchasePromo';
import { isInAppShop } from '../../lib/inAppShop';
import useFreeTrialPromoVisible from '../../hooks/useFreeTrialPromoVisible';
import useHardwareFirstShop from '../../hooks/useHardwareFirstShop';
import useIsReferralCheckoutV2 from '../../hooks/useIsReferralCheckoutV2';
import StarRating from '../StarRating/StarRating';

/**
 * Reads the router's query string rather than the address bar's: main's prerendered listing renders at
 * its bare URL first, and the router takes on the address bar's query string after hydration.
 */
function useRedeemFlow() {
  const { search } = useLocation();
  const discountParam = getUrlPartnerCode(search);
  const isRedeemFlow = !!discountParam?.trim();

  return { discountParam, isRedeemFlow };
}

type PlpCardLabel = string;

function getPlpCardLabel(title: string, subtitle?: string): PlpCardLabel {
  const series3PlusTitle = i18next.t('series_3_plus');
  const miniTitle = i18next.t('mini');
  const ultraTitle = i18next.t('fi_ultra');
  const forCatsSubtitle = i18next.t('for_cats');

  if (title === series3PlusTitle) return i18next.t('series_3_plus');
  if (title === ultraTitle) return i18next.t('fi_ultra');
  if (title === miniTitle && subtitle && subtitle.toUpperCase().includes(forCatsSubtitle.toUpperCase()))
    return i18next.t('mini_cats');
  return i18next.t('mini');
}

function trackDetailsToggle(title: string, subtitle: string | undefined, expanded: boolean) {
  const label = getPlpCardLabel(title, subtitle);
  const miniLabel = i18next.t('mini');
  const series3PlusLabel = i18next.t('series_3_plus');
  const miniCatsLabel = i18next.t('mini_cats');
  const ultraLabel = i18next.t('fi_ultra');

  if (label === miniLabel) {
    expanded ? productListingPage.miniDetailsExpand() : productListingPage.miniDetailsCollapse();
  } else if (label === series3PlusLabel) {
    expanded ? productListingPage.series3PlusDetailsExpand() : productListingPage.series3PlusDetailsCollapse();
  } else if (label === miniCatsLabel) {
    expanded ? productListingPage.miniCatsDetailsExpand() : productListingPage.miniCatsDetailsCollapse();
  } else if (label === ultraLabel) {
    expanded ? productListingPage.ultraDetailsExpand() : productListingPage.ultraDetailsCollapse();
  }
}

type BetterForProps = {
  children: React.ReactNode;
  color: string;
  icon: string;
};
const ProductCardBetterFor: React.FC<BetterForProps> = ({ children, color, icon }) => (
  <span className={classNames(styles.betterForItem, styles[color])}>
    {icon === 'dog' ? <Dog /> : <Cat />}
    {children}
  </span>
);

type CardProps = {
  title: string;
  subtitle?: string;
  description: string;
  image: string;
  cta: string;
  onClick: () => void;
  hardware?: (string | { text: string; icon: React.ReactNode })[];
  betterFor?: { color: string; label: string; icon: string }[];
  showSaleBadge?: boolean;
  freeTrialAvailable?: boolean;
  /** `v2` is the pricing-prototype card: bullets always open, full-width cta, no details drawer. */
  variant?: 'classic' | 'v2';
  /** v2 crops each photo on its own anchor so the collar stays in frame */
  imagePosition?: string;
};

const ProductCard: React.FC<CardProps> = ({
  title,
  subtitle,
  description,
  image,
  cta,
  onClick,
  hardware = [],
  betterFor = [],
  showSaleBadge = false,
  freeTrialAvailable = false,
  variant = 'classic',
  imagePosition,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const hasDetails = hardware.length > 0 || betterFor.length > 0;

  if (variant === 'v2') {
    return (
      <div className={v2.card}>
        <div className={v2.cardShot}>
          <img src={image} alt={title} loading="lazy" style={{ objectPosition: imagePosition }} />
        </div>
        <div className={v2.cardBody}>
          <div className={v2.cardTitle}>{title}</div>
          <div className={v2.cardDescription}>{description}</div>
          <ul className={v2.cardBullets}>
            {hardware.map((h) => {
              const isCustom = typeof h !== 'string';
              return (
                <li key={isCustom ? h.text : h}>
                  {isCustom ? h.icon : <Check />}
                  <span>{isCustom ? h.text : h}</span>
                </li>
              );
            })}
          </ul>
          <div className={v2.cardCtaWrap}>
            <button type="button" className={v2.cardCta} onClick={onClick}>
              {cta}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.productCard}>
      <img className={styles.cardImage} src={image} alt={title} loading="lazy" />
      <div className={styles.cardBody}>
        <div className={styles.cardTitle}>
          {title}
          <div className={styles.cardTitleSubtitle}>{subtitle}</div>
          {showSaleBadge && <SaleBadge className={styles.saleBadge} />}
        </div>
        <div className={styles.cardDescription}>{description}</div>
        <div className={styles.cardButtonRow}>
          <button className={styles.cardButton} onClick={onClick}>
            {cta}
          </button>
          {freeTrialAvailable && <div className={styles.freeTrialAvailableText}>{t('free_trial')}</div>}
        </div>
        {hasDetails && (
          <>
            <div className={styles.divider} />
            <button
              type="button"
              className={styles.detailsToggle}
              aria-expanded={showDetails}
              onClick={() =>
                setShowDetails((s) => {
                  const next = !s;
                  trackDetailsToggle(title, subtitle, next);
                  return next;
                })
              }
            >
              <div className={styles.closeContainer}>
                <span className={styles.closeContainerTextMobile}>{t('hide_details')}</span>
                <span className={styles.closeContainerTextDesktop}>{t('details')}</span>
                <UpArrow />
              </div>
              <div className={styles.openContainer}>
                <span className={styles.openContainerTextMobile}>{t('see_details')}</span>
                <span className={styles.openContainerTextDesktop}>{t('details')}</span>
                <DownArrow />
              </div>
            </button>

            <div
              className={classNames(styles.detailsSection, {
                [styles.open]: showDetails,
              })}
            >
              <div className={styles.hardwareTitle}>{t('hardware_no_colon')}</div>
              <div className={styles.hardware}>
                {hardware.map((h) => {
                  const isCustom = typeof h !== 'string';
                  return (
                    <div className={styles.hardwareItem} key={isCustom ? h.text : h}>
                      {isCustom ? h.icon : <Check />}
                      {isCustom ? h.text : h}
                    </div>
                  );
                })}
              </div>
              <div className={styles.betterForTitle}>{t('better_for')}</div>
              <div className={styles.betterFor}>
                {betterFor.map((a) => (
                  <ProductCardBetterFor key={a.label} color={a.color} icon={a.icon}>
                    {a.label}
                  </ProductCardBetterFor>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ProductCarousel: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => <div className={classNames(styles.carousel, className)}>{children}</div>;

type OrderableProductCard = {
  key: string;
  visible: boolean;
  node: React.ReactNode;
};

/**
 * Orders the main product cards by the `o` query param (e.g. `?o=u-s3-m-mc`).
 * Tokens are hyphen-separated card keys; unknown or duplicate tokens are
 * ignored, and any visible card the param omits is appended in its default
 * position so a partial `o` never silently hides a product. Hidden cards
 * (country/flow gated) are never surfaced even when explicitly requested.
 * Falls back to the default order when the param is absent.
 */
function orderProductCards(cards: OrderableProductCard[], orderParam: string | null): OrderableProductCard[] {
  const visible = cards.filter((c) => c.visible);
  if (!orderParam) return visible;

  const byKey = new Map(visible.map((c) => [c.key, c]));
  const seen = new Set<string>();
  const ordered = orderParam
    .split('-')
    .map((token) => token.trim().toLowerCase())
    .flatMap((key) => {
      const card = byKey.get(key);
      if (!card || seen.has(key)) return [];
      seen.add(key);
      return [card];
    });

  return [...ordered, ...visible.filter((c) => !seen.has(c.key))];
}

const ProductListingPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const launchQuiz = useLaunchQuiz();
  const { search } = useLocation();
  const scrollFiredRef = useRef<{ [k: string]: boolean }>({
    '50': false,
    '75': false,
    '100': false,
  });
  const { country } = useLocale();
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);
  const { discountParam, isRedeemFlow } = useRedeemFlow();
  // If the `ecom-partner-landing-pages` GrowthBook flag is on (prod) or we're
  // in a non-prod build, and the partner code's prefix maps to a registered
  // partner landing page, redirect there while preserving the full query
  // string (so `?pc=...` is kept).
  const partnerLandingEnabled = usePartnerLandingEnabled();
  const partnerSlugRedirect = partnerLandingEnabled ? getPartnerSlugForCode(discountParam) : null;
  const isUltraSupportedInCountry = useIsGrowthBookEnabled() && ultraIsDiscoverable(country, internalTester);
  const isFreeTrialVisible = useFreeTrialPromoVisible();
  const inApp = isInAppShop();
  const isReferralV2 = useIsReferralCheckoutV2();
  const referrerName = useSelector((state: types.AppState) => state.storeShop.cart.referrerName);
  // in the hardware-first arm the tracker cards open /shop narrowed to that one device, rather
  // than that device's own pdp: the shopper already picked here, so the shop shows no rail to
  // re-pick from. marketing keeps this page on the classic listing either way.
  const shopEnabled = useHardwareFirstShop();
  // the pricing-prototype redesign is the plain listing. the redeem, referral and in-app variants
  // carry their own copy and keep the classic layout.
  const plpV2 = !isRedeemFlow && !isReferralV2 && !inApp;
  const flagsLoaded = useFlagsLoaded();
  // Main's prerendered listing is on screen before its router holds the address bar's query string,
  // and a `pdp=` landing (see ProductsRoute) is no listing view at all, so the view waits for it.
  const holdsBrowserLocation = typeof window !== 'undefined' && search === window.location.search;

  useEffect(() => {
    if (!holdsBrowserLocation) {
      return;
    }
    if (partnerSlugRedirect) {
      partnerLandingPage.redirectedFromPlp(partnerSlugRedirect);
      // Skip PLP analytics + scroll tracking for redirected sessions so the
      // partner landing gets the page-view signal instead of the PLP.
      return;
    }
    productListingPage.pageViewed();
    if (inApp) {
      inAppShop.opened();
    }

    const onScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const height = (document.documentElement.scrollHeight || 0) - window.innerHeight;
      if (height <= 0) {
        return;
      }
      const pct = (scrollTop / height) * 100;
      if (!scrollFiredRef.current['50'] && pct >= 50) {
        productListingPage.scroll50();
        scrollFiredRef.current['50'] = true;
      }
      if (!scrollFiredRef.current['75'] && pct >= 75) {
        productListingPage.scroll75();
        scrollFiredRef.current['75'] = true;
      }
      if (!scrollFiredRef.current['100'] && pct >= 100) {
        productListingPage.scroll100();
        scrollFiredRef.current['100'] = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true } as any);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll as any);
  }, [holdsBrowserLocation, partnerSlugRedirect, inApp]);

  // Country gating runs BEFORE the partner redirect so that non-Mini countries
  // continue to land on the Series 3+ PDP as they did before this feature.
  // Partner pages surface a Mini CTA unconditionally, so redirecting Mini-
  // unsupported countries into them would leak a broken call-to-action.
  // (Today the only Mini-supported country is US — see LocaleContext.)
  // Main's prerendered listing renders before the flags load, when only the US counts as a Mini market.
  if (flagsLoaded && !collarProductIsSupportedInCountry(types.miniCollarId, country)) {
    return <Redirect to={AppPaths.Products(types.series3PlusCollarId)} />;
  }

  if (partnerSlugRedirect) {
    return <Redirect to={`${AppPaths.Partners.LandingFor(partnerSlugRedirect)}${search}`} />;
  }

  const miniBatteryLife = t('up_to_2_months_battery_life');
  const variant = plpV2 ? 'v2' : 'classic';
  // v2 bullets always show, so every row carries an icon
  const spec = (icon: PlpSpecIcon, text: string) => ({
    text,
    icon: <PlpIcon name={icon} />,
  });
  const attachable = spec('link', t('attachable_to_collars_and_harnesses'));
  const lightweight = spec('feather', t('small_and_lightweight'));
  const miniBattery = spec('batt', miniBatteryLife);
  const intelligence = spec('magic', t('fi_intelligence_enabled'));

  // Card display order can be overridden via `?o=` (e.g. `?o=u-s3-m-mc`). v2 leads with mini.
  const orderParam = new URLSearchParams(search).get('o') ?? (plpV2 ? 'm-s3-u-mc' : null);
  const productCards: OrderableProductCard[] = [
    {
      key: 's3',
      visible: true,
      node: (
        <ProductCard
          variant={variant}
          title={plpV2 ? t('plp_v2_card_series_3_plus') : 'Series 3+'}
          description={plpV2 ? t('plp_v2_desc_series_3_plus') : t('product_description_series_3_plus')}
          image={FiSeries3PlusImage}
          imagePosition="center 40%"
          cta={isRedeemFlow ? t('claim_free_series_3_plus') : t('shop_fi_series_3_plus')}
          onClick={() => {
            productListingPage.series3PlusShopNow();
            history.push(
              isRedeemFlow
                ? `${AppPaths.Redeem}?discount=${encodeURIComponent(discountParam ?? '')}`
                : shopEnabled
                  ? AppPaths.ShopForDeviceOnly('s3')
                  : AppPaths.Products(series3PlusCollarId),
            );
          }}
          hardware={
            plpV2
              ? [
                  spec('collar', t('built_directly_into_the_collar')),
                  spec('shield', t('high_durability')),
                  spec('batt', t('up_to_3_months_of_battery_life')),
                  intelligence,
                ]
              : [
                  t('high_durability'),
                  t('built_directly_into_the_collar'),
                  t('up_to_3_months_of_battery_life'),
                  intelligence,
                ]
          }
          betterFor={[
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
            { color: 'green', label: t('large_dogs'), icon: 'dog' },
            { color: 'green', label: t('extra_large_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={!isRedeemFlow && !inApp && isFreeTrialVisible}
        />
      ),
    },
    {
      key: 'm',
      visible: true,
      node: (
        <ProductCard
          variant={variant}
          title={plpV2 ? t('plp_v2_card_mini') : 'Mini'}
          description={plpV2 ? t('plp_v2_desc_mini') : t('product_description_mini')}
          image={FiMiniImage}
          imagePosition="center 46%"
          cta={isRedeemFlow ? t('claim_free_mini') : t('shop_fi_mini')}
          onClick={() => {
            productListingPage.miniShopNow();
            history.push(
              isRedeemFlow
                ? `${AppPaths.MiniRedeem}?discount=${encodeURIComponent(discountParam ?? '')}&type=dog`
                : shopEnabled
                  ? AppPaths.ShopForDeviceOnly('mini')
                  : AppPaths.Products('fi-mini') + '?type=dog',
            );
          }}
          hardware={
            plpV2
              ? [attachable, lightweight, miniBattery, intelligence]
              : [t('attachable_to_collars_and_harnesses'), t('small_and_lightweight'), miniBatteryLife, intelligence]
          }
          betterFor={[
            { color: 'yellow', label: t('small_dogs'), icon: 'dog' },
            { color: 'yellow', label: t('puppies'), icon: 'dog' },
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={!isRedeemFlow && !inApp && isFreeTrialVisible}
        />
      ),
    },
    {
      key: 'u',
      visible: isUltraSupportedInCountry && !isRedeemFlow,
      node: (
        <ProductCard
          variant={variant}
          title={plpV2 ? t('plp_v2_card_ultra') : 'Ultra'}
          description={t('product_description_ultra')}
          image={FiUltraImage}
          imagePosition="72% 34%"
          cta={t('shop_fi_ultra')}
          onClick={() => {
            productListingPage.ultraShopNow();
            history.push(shopEnabled ? AppPaths.ShopForDeviceOnly('ultra') : AppPaths.Products('fi-ultra'));
          }}
          hardware={
            plpV2
              ? [
                  attachable,
                  spec('satellite', t('satellite_tracking_with_starlink')),
                  spec('batt', t('plp_v2_up_to_5_days_of_battery')),
                  spec('vibe', t('sound_and_vibration_feedback')),
                ]
              : [
                  t('attachable_to_collars_and_harnesses'),
                  t('satellite_tracking_with_starlink'),
                  t('sound_and_vibration_feedback'),
                ]
          }
          betterFor={[
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
            { color: 'green', label: t('large_dogs'), icon: 'dog' },
            { color: 'green', label: t('extra_large_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={false}
        />
      ),
    },
    {
      key: 'mc',
      visible: !isRedeemFlow && !isReferralV2,
      node: (
        <ProductCard
          variant={variant}
          title={plpV2 ? t('plp_v2_card_mini_cats') : 'Mini'}
          subtitle={plpV2 ? undefined : t('for_cats')}
          description={t('product_description_mini_cats')}
          image={FiMiniForCatsImage}
          imagePosition="center 38%"
          cta={plpV2 ? t('plp_v2_shop_fi_mini_cats') : t('shop_fi_mini')}
          onClick={() => {
            productListingPage.miniCatsShopNow();
            history.push(shopEnabled ? AppPaths.ShopForCats : AppPaths.Products('fi-mini') + '?type=cat');
          }}
          hardware={
            plpV2
              ? [attachable, lightweight, miniBattery, intelligence]
              : [t('attachable_to_collars_and_harnesses'), t('small_and_lightweight'), miniBatteryLife]
          }
          betterFor={[{ color: 'yellow', label: t('cats'), icon: 'cat' }]}
          showSaleBadge={true}
          freeTrialAvailable={!isRedeemFlow && !inApp && isFreeTrialVisible}
        />
      ),
    },
  ];
  const orderedProductCards = orderProductCards(productCards, orderParam);

  const keepShopping = [
    {
      title: t('accessories'),
      description: t('shop_bases_collars_attachments_and_more'),
      image: FiAccessoriesImage,
      visible: true,
      onClick: () => {
        productListingPage.keepShoppingAccessories();
        history.push(AppPaths.Accessories);
      },
    },
    {
      title: t('nano_microchips'),
      description: t('microchips_for_vets_and_rescue_shelters'),
      image: FiNanoMicrochipsImage,
      visible: nanoProductIsSupportedInCountry(country),
      onClick: () => {
        productListingPage.keepShoppingNano();
        window.location.href = `https://${getDomainConfig().nanoDomain}/`;
      },
    },
  ];

  const openQuiz = () => {
    quizQuestionPage.quizButtonTapped();
    launchQuiz();
  };
  const openCompare = () => {
    productListingPage.comparisonCompareProducts();
    history.push(AppPaths.Compare);
  };
  const visibleKeepShopping = keepShopping.filter((item) => item.visible);

  if (plpV2) {
    const cardCols = ['', '', v2.two, v2.three, ''][orderedProductCards.length] ?? '';
    return (
      <div className={classNames('product-listing-page', v2.ground)}>
        <QuizAutoOpen />
        <div className={v2.page}>
          <header className={v2.hero}>
            <div className={v2.eyebrow}>{t('plp_v2_eyebrow')}</div>
            <h1 className={v2.title}>{t('plp_v2_title')}</h1>
            <p className={v2.subtitle}>{t('plp_v2_subtitle')}</p>
            <div className={v2.stars}>
              <span className={v2.starGlyphs} aria-hidden="true">
                {'★★★★★'}
              </span>
              <span>
                {'4.7 · '}
                {t('star_rating_reviews_count')}
              </span>
            </div>
          </header>

          <div className={v2.quizBanner}>
            <span className={v2.quizSpark}>
              <QuizSpark />
            </span>
            <div className={v2.quizText}>
              <b>{t('plp_v2_quiz_title')}</b>
              <span>{t('plp_v2_quiz_body')}</span>
            </div>
            <button type="button" className={v2.quizCta} onClick={openQuiz}>
              {t('plp_v2_quiz_cta')}
            </button>
          </div>
          <button type="button" className={v2.quizRow} onClick={openQuiz}>
            <span className={v2.quizSpark}>
              <QuizSpark />
            </span>
            <span className={v2.quizRowLabel}>
              <b>{t('plp_v2_quiz_row_lead')}</b> {t('plp_v2_quiz_row_cta')}
            </span>
            <span className={v2.quizRowArrow} aria-hidden="true">
              {'→'}
            </span>
          </button>

          <div className={classNames(v2.grid, cardCols)}>
            {orderedProductCards.map((card) => (
              <React.Fragment key={card.key}>{card.node}</React.Fragment>
            ))}
          </div>

          <div className={v2.trustBlock}>
            <div className={v2.trustLabel}>{t('plp_v2_included_with_every_fi')}</div>
            <div className={v2.trustRow}>
              {[
                [t('free_shipping'), t('plp_v2_shipping_sub')],
                [t('hardware_first_returns_title'), t('plp_v2_returns_sub')],
                [t('one_year_warranty'), t('cart_item_v2.warranty_description')],
              ].map(([title, sub]) => (
                <div key={title} className={v2.trustTile}>
                  <div className={v2.trustTitle}>{title}</div>
                  <div className={v2.trustSub}>{sub}</div>
                </div>
              ))}
            </div>
          </div>

          <div className={v2.block}>
            <div className={v2.compareBand}>
              <div className={v2.compareText}>
                <div className={v2.compareTitle}>{t('not_sure_which_fi_is_for_you')}</div>
                <div className={v2.compareBody}>{t('plp_v2_compare_body')}</div>
              </div>
              <div className={v2.compareButtons}>
                <button type="button" className={v2.compareHelp} onClick={openQuiz}>
                  {t('help_me_choose')}
                </button>
                <button type="button" className={v2.compareAll} onClick={openCompare}>
                  {t('plp_v2_compare_all_products')}
                </button>
              </div>
            </div>
          </div>

          {isFreeTrialVisible && (
            <FreeTrialCard
              onStartFreeTrial={() => {
                productListingPage.freeTrialStartFreeTrial();
                launchQuiz();
              }}
            />
          )}

          <div className={v2.block}>
            <div className={v2.blockTitle}>{t('keep_shopping')}</div>
            <div
              className={classNames(v2.shopRows, {
                [v2.three]: visibleKeepShopping.length === 3,
              })}
            >
              {visibleKeepShopping.map((item) => (
                <button key={item.title} type="button" className={v2.shopRow} onClick={item.onClick}>
                  <span className={v2.shopThumb}>
                    <img src={item.image} alt="" loading="lazy" />
                  </span>
                  <span className={v2.shopRowBody}>
                    <span className={v2.shopRowTitle}>
                      {item.title}
                      <span className={v2.shopRowArrow} aria-hidden="true">
                        {'→'}
                      </span>
                    </span>
                    <span className={v2.shopRowSub}>{item.description}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('product-listing-page', styles.container)}>
      <QuizAutoOpen />
      <Flex direction={'column'} gap={16} align="center">
        <Text fz={isReferralV2 ? 36 : 56} fw={700} lh={1} lts="-1.12px" ta={isReferralV2 ? 'center' : undefined}>
          {isRedeemFlow ? t('exclusive_offer_claim_your_free_fi_collar') : t('trusted_by_millions_of_pet_parents')}
        </Text>
        <Text fz={20} fw={700} lh={1.2} lts="-0.4px" c="type-light-secondary" ta={isReferralV2 ? 'center' : undefined}>
          {isRedeemFlow
            ? t('99_off_applied_at_checkout')
            : isReferralV2
              ? referrerName
                ? t('referral_v2_plp_subline', { referrerName })
                : t('referral_v2_plp_subline_generic')
              : t('most_advanced_gps_tracking_solutions_for_your_sidekick')}
        </Text>
        {isReferralV2 && <StarRating />}
        {!isRedeemFlow && !isReferralV2 && (
          <Button
            bg={'black'}
            radius={100}
            className={styles.quizPromptButton}
            onClick={() => {
              quizQuestionPage.quizButtonTapped();
              launchQuiz();
            }}
          >
            <Text fz={14} fw={600} lh={1.2} py={10} px={24} lts="-0.14px">
              {t('which_fi_is_right_for_me')}
            </Text>
          </Button>
        )}
      </Flex>
      <ProductCarousel>
        {orderedProductCards.map((card) => (
          <React.Fragment key={card.key}>{card.node}</React.Fragment>
        ))}
      </ProductCarousel>

      {!isReferralV2 && (
        <div className={styles.compareBox}>
          <div>
            <div className={styles.compareTitle}>{t('not_sure_which_fi_is_for_you')}</div>
          </div>
          <div className={styles.compareButtonsContainer}>
            <button
              className={styles.helpMeChooseCta}
              onClick={() => {
                quizQuestionPage.quizButtonTapped();
                launchQuiz();
              }}
            >
              {t('help_me_choose')}
            </button>
            <button
              className={styles.compareCta}
              onClick={() => {
                productListingPage.comparisonCompareProducts();
                history.push(AppPaths.Compare);
              }}
            >
              {t('compare_products')}
            </button>
          </div>
        </div>
      )}

      {isFreeTrialVisible && !isRedeemFlow && !inApp && !isReferralV2 && (
        <FreeTrialCard
          onStartFreeTrial={() => {
            productListingPage.freeTrialStartFreeTrial();
            launchQuiz();
          }}
        />
      )}

      {!isReferralV2 && (
        <>
          <div className={styles.keepShoppingTitle}>{t('keep_shopping')}</div>
          <ProductCarousel>
            {keepShopping
              .filter((item) => item.visible)
              .map((item) => (
                <ProductCard key={item.title} {...item} cta={t('shop')} />
              ))}
          </ProductCarousel>
        </>
      )}
      <div className={styles.footer} />
    </div>
  );
};

export default ProductListingPage;
